import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useYacht } from "../../context/YachtContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  DOC_KINDS, PAYMENT_METHODS, CONTRACT_TYPES, SALARY_PERIODS,
  paymentMethodLabel, payTypeLabel, titleCase, STATUS_STYLE, statusLabel,
} from "../../lib/crewHr";
import { Loader2, FileText, AlertTriangle, IdCard, ShieldCheck, CheckCircle2 } from "lucide-react";

const fmtErr = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
const labelOf = (list, key) => (list.find((x) => x.key === key) || {}).label || titleCase(key);

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-xs font-medium text-zinc-500 mb-0.5">{label}</div>
    <div className={`text-sm text-zinc-800 ${mono ? "font-mono" : ""}`} data-testid={`myhr-field-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {value != null && value !== "" ? value : <span className="text-zinc-400">—</span>}
    </div>
  </div>
);

export default function MyHR() {
  const { activeYacht } = useYacht();
  const yachtId = activeYacht?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sp] = useSearchParams();
  const TABS = ["employment", "profile", "documents", "payroll"];
  const [tab, setTab] = useState(TABS.includes(sp.get("tab")) ? sp.get("tab") : "employment");

  const load = useCallback(async () => {
    if (!yachtId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/crew/me/hr?yacht_id=${yachtId}`);
      setData(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setLoading(false); }
  }, [yachtId]);
  useEffect(() => { load(); }, [load]);

  const confirmPay = async (id) => {
    try {
      const { data: updated } = await api.post(`/crew/me/paylog/${id}/confirm`);
      setData((d) => (d ? { ...d, paylog: (d.paylog || []).map((e) => (e.id === id ? { ...e, ...updated } : e)) } : d));
      toast.success("Confirmed — thanks!");
      window.dispatchEvent(new Event("seavanto:badges-refresh"));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-500 p-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading your HR record…</div>;
  }
  if (!yachtId) {
    return <div className="p-6 lg:p-10 text-sm text-zinc-500" data-testid="myhr-no-yacht">Select a yacht to view your HR record.</div>;
  }

  const p = data?.profile || {};
  const emp = data?.employment || {};
  const docs = data?.documents || [];
  const pay = data?.paylog || [];
  const alerts = data?.cert_alerts || [];
  const payMethods = (emp.payment_methods || []).map((k) => labelOf(PAYMENT_METHODS, k)).join(", ")
    || (emp.payment_method ? titleCase(emp.payment_method) : "");
  const perks = [
    ["Health insurance", emp.health_insurance_provided],
    ["Visa handled by company", emp.visa_handled_by_company],
    ["Accommodation provided", emp.accommodation_provided],
    ["Travel costs covered", emp.travel_costs_covered],
  ].filter(([, v]) => v);

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="my-hr-page">
      <div className="flex items-center gap-2 mb-1">
        <IdCard className="h-5 w-5 text-zinc-500" />
        <h1 className="font-display text-2xl">My HR</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-5">
        Your employment record on <span className="font-medium text-zinc-700">{data?.yacht_name || "this yacht"}</span>. This is read-only — contact your manager to make changes.
      </p>

      {alerts.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex flex-wrap items-center gap-2" data-testid="myhr-cert-alerts">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          {alerts.map((a, i) => <Badge key={i} className={`${STATUS_STYLE[a.status]} border`}>{a.cert_label}: {statusLabel(a.status, a.days_left)}</Badge>)}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="max-w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="employment" data-testid="myhr-tab-employment">Employment</TabsTrigger>
          <TabsTrigger value="profile" data-testid="myhr-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="documents" data-testid="myhr-tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="myhr-tab-payroll">Payroll</TabsTrigger>
        </TabsList>

        <TabsContent value="employment" className="mt-4 space-y-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Internal crew ID" value={emp.internal_crew_id} mono />
            <Field label="Contract type" value={emp.contract_type ? labelOf(CONTRACT_TYPES, emp.contract_type) : ""} />
            <Field label="Start date" value={emp.start_date} />
            <Field label="End date" value={emp.end_date} />
            <Field label="Salary" value={emp.salary_agreed ? `${emp.salary_agreed} ${emp.salary_period ? labelOf(SALARY_PERIODS, emp.salary_period) : ""}`.trim() : ""} />
            <Field label="Payment methods" value={payMethods} />
          </div>
          {perks.length > 0 && (
            <div className="rounded-xl border border-zinc-200 bg-white p-5 flex flex-wrap gap-2" data-testid="myhr-perks">
              {perks.map(([l]) => (
                <Badge key={l} className="bg-emerald-50 text-emerald-700 border border-emerald-200"><ShieldCheck className="h-3 w-3 mr-1" />{l}</Badge>
              ))}
            </div>
          )}
          {(emp.bank_iban || emp.bank_name || emp.bank_account_holder) && (
            <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="myhr-bank">
              <h3 className="font-display text-base mb-4">Bank details on file</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Account holder" value={emp.bank_account_holder} />
                <Field label="Bank name" value={emp.bank_name} />
                <Field label="IBAN" value={emp.bank_iban} mono />
                <Field label="BIC / SWIFT" value={emp.bank_bic_swift} mono />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile" className="mt-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Full name" value={p.full_name} />
            <Field label="Position" value={p.position} />
            <Field label="Nationality" value={p.nationality} />
            <Field label="Phone" value={p.phone} />
            <Field label="Email" value={p.email} />
            <Field label="Passport expiry" value={p.passport_expiry_date} />
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-5">
            {docs.length === 0 ? <p className="text-sm text-zinc-400">No documents stored.</p> : (
              <div className="divide-y divide-zinc-100">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between py-2.5" data-testid={`myhr-doc-${d.id}`}>
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="h-4 w-4 text-zinc-500" />
                      <a className="text-blue-600 hover:underline" target="_blank" rel="noreferrer" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.file_path}`}>{d.name || "Document"}</a>
                      <Badge variant="outline" className="text-[10px]">{labelOf(DOC_KINDS, d.kind)}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payroll" className="mt-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-5">
            {pay.length === 0 ? <p className="text-sm text-zinc-400">No payroll entries.</p> : (
              <div className="divide-y divide-zinc-100 text-sm">
                {pay.map((e) => (
                  <div key={e.id} className="flex items-center justify-between gap-3 py-2" data-testid={`myhr-pay-${e.id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-zinc-500">{e.date}</span>
                      <Badge variant="outline" className="text-[10px]">{payTypeLabel(e.type)}</Badge>
                      {e.quantity != null ? <span>{e.quantity} {e.unit || "days"}</span>
                        : e.period ? <span className="text-zinc-500">{e.period}</span>
                        : e.hours ? <span>{e.hours}h</span> : null}
                      {e.amount != null ? <span className="font-medium">{e.amount}</span> : null}
                      {e.payment_method ? <Badge className="bg-zinc-100 text-zinc-700">{paymentMethodLabel(e.payment_method)}</Badge> : null}
                      {e.note ? <span className="text-zinc-400">{e.note}</span> : null}
                    </div>
                    <div className="shrink-0">
                      {e.requires_confirmation
                        ? (e.confirmed
                            ? <Badge className="bg-emerald-100 text-emerald-700" data-testid={`myhr-pay-received-${e.id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Received{e.confirmed_at ? ` · ${String(e.confirmed_at).slice(0, 10)}` : ""}</Badge>
                            : <Button size="sm" className="h-7 bg-ink hover:bg-black text-xs" onClick={() => confirmPay(e.id)} data-testid={`myhr-pay-confirm-${e.id}`}>Confirm received</Button>)
                        : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
